import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import inductionApi, { clearInductionToken } from '../api/inductionAxios';
import { T, PageHeader, Section, Field, Alert } from '../components/UI';
import SignaturePad from '../components/SignaturePad';
import {
  INDUCTEE_FIELDS, CHECKLIST_SECTIONS, CHECKLIST_STEPS, DECLARATION_TEXT, RETENTION_NOTE,
  TOTAL_CHECKLIST_ITEMS, buildEmptyChecklist, countChecked,
} from '../constants/staffInductionChecklist';

const todayISO = () => new Date(new Date().getTime() + 8*60*60*1000).toISOString().split('T')[0];

const SECTION_BY_KEY = Object.fromEntries(CHECKLIST_SECTIONS.map(s => [s.key, s]));

// Filled in by the manager when they send the invite — the worker sees but can't change these
const WORKER_LOCKED_FIELDS = ['workerName', 'position', 'startDate', 'site', 'inductedBy'];

const WIZARD_STEPS = [
  { key:'details', title:'Inductee Details', kind:'details' },
  ...CHECKLIST_STEPS.map(s => ({ key:s.title, title:s.title, kind:'checklist', sectionKeys:s.sectionKeys })),
  { key:'signoff', title:'Sign-off Declaration', kind:'signoff' },
];

const CheckRow = ({ label, checked, onToggle }) => (
  <div onClick={onToggle}
    style={{ display:'flex', alignItems:'flex-start', gap:'10px', padding:'8px 0', borderBottom:'1px solid var(--bg)', cursor:'pointer' }}>
    <div style={{ width:'20px', height:'20px', borderRadius:'4px', border:`2px solid ${checked?'var(--blue)':'var(--border-md)'}`, background:checked?'var(--blue)':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:'1px' }}>
      {checked && <i className="ti ti-check" style={{ fontSize:'13px', color:'#fff' }} aria-hidden="true"/>}
    </div>
    <span style={{ fontSize:'13px', color:'var(--text)', lineHeight:1.5 }}>{label}</span>
  </div>
);

export default function StaffInductionForm({ existingData, editMode = false, inductionId, workerMode = false }) {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [siteLocations, setSiteLocations] = useState([]);
  const [fields,    setFields]    = useState({ workerName:'', position:'', startDate:'', site:'', inductedBy:user?.name||'', emergencyContact:'', medicalNotes:'' });
  const [checklist, setChecklist] = useState(buildEmptyChecklist);
  const [workerSignatureName,     setWorkerSignatureName]     = useState('');
  const [workerSignature,         setWorkerSignature]         = useState('');
  const [supervisorSignatureName, setSupervisorSignatureName] = useState(user?.name || '');
  const [supervisorSignature,     setSupervisorSignature]     = useState('');

  useEffect(() => {
    if (!existingData) return;
    setFields({
      workerName:       existingData.workerName || '',
      position:          existingData.position || '',
      startDate:         existingData.startDate ? existingData.startDate.split('T')[0] : '',
      site:              existingData.site || '',
      inductedBy:        existingData.inductedBy || '',
      emergencyContact:  existingData.emergencyContact || '',
      medicalNotes:      existingData.medicalNotes || '',
    });
    setChecklist(c => ({ ...c, ...(existingData.checklist || {}) }));
    setWorkerSignatureName(existingData.workerSignatureName || (workerMode ? existingData.workerName : '') || '');
    setWorkerSignature(existingData.workerSignature || '');
    setSupervisorSignatureName(existingData.supervisorSignatureName || user?.name || '');
    setSupervisorSignature(existingData.supervisorSignature || '');
    // A manager opening a worker's submission is there to countersign, so start at the sign-off step
    if (!workerMode && existingData.status === 'awaiting_supervisor') setStepIndex(WIZARD_STEPS.length - 1);
  }, [existingData]);

  const [stepIndex,  setStepIndex]  = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [error,      setError]      = useState(null);
  const [fieldErrors,setFieldErrors]= useState({});

  useEffect(() => {
    // Public endpoint — also what lets a worker pick their site when the invite didn't include one
    api.get('/jobs/locations')
      .then(({ data }) => setSiteLocations((data.locations || []).slice().sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => {});
  }, []);

  const setField = (k, v) => { setFields(f => ({ ...f, [k]: v })); setFieldErrors(fe => ({ ...fe, [k]: false })); };
  const toggleItem = (sectionKey, idx) => {
    setChecklist(c => {
      const next = [...c[sectionKey]];
      next[idx] = !next[idx];
      return { ...c, [sectionKey]: next };
    });
    setFieldErrors(fe => ({ ...fe, [sectionKey]: false }));
  };

  const checkedCount = countChecked(checklist);
  const step = WIZARD_STEPS[stepIndex];
  const isLastStep = stepIndex === WIZARD_STEPS.length - 1;

  // A worker can't edit what the invite already filled in; anything left blank they complete themselves
  const isLocked = (key) => workerMode && WORKER_LOCKED_FIELDS.includes(key) && !!existingData?.[key];

  // ── Validate a single step — returns its error keys ──
  const getStepErrors = (s) => {
    const errs = {};
    if (s.kind === 'details') {
      INDUCTEE_FIELDS.forEach(f => { if (f.required && !fields[f.key]?.trim()) errs[f.key] = true; });
    } else if (s.kind === 'checklist') {
      s.sectionKeys.forEach(key => { if (checklist[key].some(v => !v)) errs[key] = true; });
    } else if (s.kind === 'signoff') {
      if (!workerSignatureName.trim()) errs.workerSignatureName = true;
      if (!workerSignature) errs.workerSignature = true;
      if (!workerMode) {
        if (!supervisorSignatureName.trim()) errs.supervisorSignatureName = true;
        if (!supervisorSignature) errs.supervisorSignature = true;
      }
    }
    return errs;
  };

  const validateAll = () => {
    let allErrs = {};
    WIZARD_STEPS.forEach(s => { allErrs = { ...allErrs, ...getStepErrors(s) }; });
    return allErrs;
  };

  const goNext = () => {
    const errs = getStepErrors(step);
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setError(step.kind === 'checklist' ? 'Please tick every item before continuing.' : 'Please complete all required fields.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setError(null); setFieldErrors({});
    setStepIndex(i => Math.min(i + 1, WIZARD_STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => {
    if (stepIndex === 0) { navigate(-1); return; }
    setError(null); setFieldErrors({});
    setStepIndex(i => Math.max(i - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    const errs = validateAll();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setError(workerMode ? 'Please complete every field, tick all checklist items, and add your signature (press “Use this signature”).' : 'Please complete every field, tick all checklist items, and add both signatures (press “Use this signature” on each).');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSubmitting(true); setError(null);
    try {
      if (workerMode) {
        await inductionApi.put('/submit', {
          position:         fields.position,
          site:             fields.site,
          emergencyContact: fields.emergencyContact,
          medicalNotes:     fields.medicalNotes,
          checklist,
          workerSignatureName,
          workerSignature,
        });
        clearInductionToken();
        setSubmitted(true);
        return;
      }
      const payload = {
        ...fields,
        checklist,
        workerSignatureName,
        workerSignature,
        workerSignedDate: existingData?.workerSignedDate || todayISO(),
        supervisorSignatureName,
        supervisorSignature,
        supervisorSignedDate: existingData?.supervisorSignedDate || todayISO(),
      };
      if (editMode && inductionId) {
        await api.patch(`/staff-inductions/${inductionId}`, payload);
      } else {
        await api.post('/staff-inductions', payload);
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted && workerMode) {
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Inter,sans-serif', padding:'20px' }}>
        <div style={{ textAlign:'center', maxWidth:'360px' }}>
          <div style={{ width:'56px', height:'56px', borderRadius:'50%', background:'var(--green-bg)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
            <i className="ti ti-check" style={{ fontSize:'28px', color:'var(--green)' }} aria-hidden="true"/>
          </div>
          <h2 style={{ fontSize:'18px', fontWeight:'500', color:'var(--text)', marginBottom:'8px' }}>Induction submitted</h2>
          <p style={{ color:'var(--muted)', fontSize:'13px', lineHeight:1.6 }}>
            Thanks {fields.workerName.split(' ')[0]}. Your supervisor will review and sign it off. Your temporary login has now been switched off, so you can close this page.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Inter,sans-serif', padding:'20px' }}>
        <div style={{ textAlign:'center', maxWidth:'360px' }}>
          <div style={{ width:'56px', height:'56px', borderRadius:'50%', background:'var(--green-bg)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
            <i className="ti ti-check" style={{ fontSize:'28px', color:'var(--green)' }} aria-hidden="true"/>
          </div>
          <h2 style={{ fontSize:'18px', fontWeight:'500', color:'var(--text)', marginBottom:'8px' }}>{editMode ? 'Induction Updated' : 'Induction Recorded'}</h2>
          <p style={{ color:'var(--muted)', marginBottom:'24px', fontSize:'13px' }}>{fields.workerName}'s induction has been saved.</p>
          <div style={{ display:'flex', gap:'10px', justifyContent:'center' }}>
            <button onClick={()=>navigate('/staff-induction')} style={T.btnSecondary}>View All</button>
            {editMode ? (
              <button onClick={()=>navigate(`/staff-induction/${inductionId}`)} style={T.btnPrimary}>View Induction</button>
            ) : (
              <button onClick={()=>window.location.reload()} style={T.btnPrimary}>New Induction</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={T.page}>
      <PageHeader title={workerMode ? 'Car Saloon — Staff Induction' : editMode ? 'Edit Staff Induction' : 'Staff Induction'} onBack={workerMode ? undefined : ()=>navigate(-1)}/>

      <div style={{ maxWidth:'700px', margin:'0 auto', padding:'20px 16px 120px' }}>
        {error && <Alert type="error">{error}</Alert>}

        {/* Step indicator */}
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'14px 16px', marginBottom:'16px' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px' }}>
            <span style={{ fontSize:'12px', fontWeight:'500', color:'var(--blue)' }}>Step {stepIndex+1} of {WIZARD_STEPS.length} — {step.title}</span>
            <span style={{ fontSize:'11px', color:'var(--muted)', fontFamily:'JetBrains Mono,monospace' }}>{checkedCount} / {TOTAL_CHECKLIST_ITEMS} ticked</span>
          </div>
          <div style={{ display:'flex', gap:'4px' }}>
            {WIZARD_STEPS.map((s, i) => (
              <div key={s.key} style={{ flex:1, height:'4px', borderRadius:'2px', background: i < stepIndex ? 'var(--green)' : i === stepIndex ? 'var(--blue)' : 'var(--border)' }}/>
            ))}
          </div>
        </div>

        {step.kind === 'details' && (
          <Section title="Inductee Details" accent>
            <p style={{ fontSize:'12px', color:'var(--muted)', marginBottom:'14px' }}>
              {workerMode
                ? 'Please check your details below, fill in your position and site if they are blank, and add your emergency contact. On the next steps, read each item carefully and tick it once you understand it. Your supervisor will review and sign it off after you submit.'
                : 'Every new worker completes this induction with a supervisor before starting work on site. The supervisor demonstrates each item, the worker ticks it once understood, and both sign the declaration at the end.'}
            </p>
            {INDUCTEE_FIELDS.map(f => (
              <Field key={f.key} label={f.required ? `${f.label} *` : `${f.label} (optional)`} error={fieldErrors[f.key] ? 'Required' : null}>
                {isLocked(f.key) ? (
                  <input disabled value={f.type === 'date' && fields[f.key] ? fields[f.key].split('-').reverse().join('/') : fields[f.key]}
                    style={{ ...T.input, background:'var(--bg)', color:'var(--muted)', cursor:'not-allowed' }}/>
                ) : f.type === 'site' ? (
                  <select value={fields.site} onChange={e=>setField('site', e.target.value)}
                    style={{ ...T.input, borderColor:fieldErrors.site?'var(--red)':'var(--border-md)' }}>
                    <option value="">— Select site —</option>
                    {siteLocations.map(s => <option key={s._id} value={s.name}>{s.name}</option>)}
                  </select>
                ) : f.type === 'textarea' ? (
                  <textarea value={fields[f.key]} onChange={e=>setField(f.key, e.target.value)} rows={2}
                    style={{ ...T.input, resize:'vertical' }} placeholder="Optional"/>
                ) : f.type === 'date' ? (
                  <input type="date" value={fields[f.key]} onChange={e=>setField(f.key, e.target.value)}
                    style={{ ...T.input, borderColor:fieldErrors[f.key]?'var(--red)':'var(--border-md)' }}/>
                ) : (
                  <input value={fields[f.key]} onChange={e=>setField(f.key, e.target.value)}
                    style={{ ...T.input, borderColor:fieldErrors[f.key]?'var(--red)':'var(--border-md)' }}/>
                )}
              </Field>
            ))}
          </Section>
        )}

        {step.kind === 'checklist' && step.sectionKeys.map(key => {
          const section = SECTION_BY_KEY[key];
          return (
            <Section key={key} title={section.title} accent error={fieldErrors[key] ? 'All items must be ticked' : null}>
              {section.subtitle && <p style={{ fontSize:'12px', color:'var(--muted)', marginBottom:'12px' }}>{section.subtitle}</p>}
              {section.table && (
                <div style={{ overflowX:'auto', marginBottom:'14px' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', minWidth:'480px' }}>
                    <thead>
                      <tr>
                        <th style={{ ...T.th, width:'40%' }}>Task</th>
                        <th style={T.th}>Required PPE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.table.map(([task, ppe], i) => (
                        <tr key={i}>
                          <td style={{ ...T.td, fontWeight:'500' }}>{task}</td>
                          <td style={T.td}>{ppe}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {section.items.map((label, idx) => (
                <CheckRow key={idx} label={label} checked={checklist[key][idx]} onToggle={()=>toggleItem(key, idx)}/>
              ))}
            </Section>
          );
        })}

        {step.kind === 'signoff' && (
          <Section title="Sign-off Declaration" accent>
            <p style={{ fontSize:'13px', color:'var(--text)', lineHeight:1.6, marginBottom:'18px' }}>{DECLARATION_TEXT}</p>

            <div style={{ marginBottom:'22px' }}>
              <div style={{ fontSize:'12px', fontWeight:'600', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:'10px' }}>Worker</div>
              <Field label="Name *" error={fieldErrors.workerSignatureName ? 'Required' : null}>
                <input value={workerSignatureName} onChange={e=>{setWorkerSignatureName(e.target.value); setFieldErrors(fe=>({...fe,workerSignatureName:false}));}}
                  style={{ ...T.input, borderColor:fieldErrors.workerSignatureName?'var(--red)':'var(--border-md)' }} placeholder="Worker's full name"/>
              </Field>
              <SignaturePad label="Worker signature *" name={workerSignatureName} value={workerSignature}
                onChange={val=>{setWorkerSignature(val); setFieldErrors(fe=>({...fe,workerSignature:false}));}}/>
              {fieldErrors.workerSignature && <div style={{ fontSize:'11px', color:'var(--red)', marginTop:'4px' }}>Please create the signature and press “Use this signature”</div>}
            </div>

            {workerMode ? (
              <Alert type="info">Your supervisor will countersign after you submit. Once you submit, this login stops working.</Alert>
            ) : (
              <div>
                <div style={{ fontSize:'12px', fontWeight:'600', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:'10px' }}>Supervisor / Inductor</div>
                <Field label="Name *" error={fieldErrors.supervisorSignatureName ? 'Required' : null}>
                  <input value={supervisorSignatureName} onChange={e=>{setSupervisorSignatureName(e.target.value); setFieldErrors(fe=>({...fe,supervisorSignatureName:false}));}}
                    style={{ ...T.input, borderColor:fieldErrors.supervisorSignatureName?'var(--red)':'var(--border-md)' }} placeholder="Supervisor's full name"/>
                </Field>
                <SignaturePad label="Supervisor signature *" name={supervisorSignatureName} value={supervisorSignature}
                  onChange={val=>{setSupervisorSignature(val); setFieldErrors(fe=>({...fe,supervisorSignature:false}));}}/>
                {fieldErrors.supervisorSignature && <div style={{ fontSize:'11px', color:'var(--red)', marginTop:'4px' }}>Please create the signature and press “Use this signature”</div>}
              </div>
            )}

            {!workerMode && <p style={{ fontSize:'11px', color:'var(--hint)', marginTop:'18px', lineHeight:1.5 }}>{RETENTION_NOTE}</p>}
          </Section>
        )}
      </div>

      {/* Sticky bottom bar */}
      <div style={{ position:'fixed', bottom:0, left:0, right:0, background:'var(--surface)', borderTop:'1px solid var(--border)', padding:'12px 16px', paddingBottom:'calc(12px + env(safe-area-inset-bottom, 60px))', display:'flex', justifyContent:'space-between', alignItems:'center', zIndex:150 }}>
        {workerMode && stepIndex === 0 ? <span/> : (
          <button type="button" onClick={goBack} style={T.btnSecondary}>
            <i className="ti ti-arrow-left" aria-hidden="true"/> {stepIndex === 0 ? 'Cancel' : 'Back'}
          </button>
        )}
        {isLastStep ? (
          <button type="button" onClick={handleSubmit} disabled={submitting} style={{ ...T.btnPrimary, opacity:submitting?0.6:1 }}>
            {submitting ? 'Saving...' : editMode ? 'Save Changes' : 'Submit Induction'}
          </button>
        ) : (
          <button type="button" onClick={goNext} style={T.btnPrimary}>
            Next <i className="ti ti-arrow-right" aria-hidden="true"/>
          </button>
        )}
      </div>
    </div>
  );
}
